import { getOrCreateAuthStore } from '../stores/authStore';
import { getAuthenticationInfo, type GetAuthenticationInfoResponse } from './apiAuthentication';
import { getOrCreateApiSessionsServer } from './apiSessions';
import createClient, { type Client, type HeadersOptions, type Middleware } from 'openapi-fetch';

interface GetOpenapiFetchClientParams {
	readonly nakamaServiceName: string;
	readonly additionalHeaders?: Record<string, string>;
	readonly baseUrl: string;
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- TODO: refactor to not use `Function` type
export type DeepReadonly<T> = T extends Function
	? T
	: T extends Array<infer U>
		? ReadonlyArray<DeepReadonly<U>>
		: T extends object
			? { readonly [K in keyof T]: DeepReadonly<T[K]> }
			: T;

type NakamaBaseUrlWithAuthenticationPath = `${GetAuthenticationInfoResponse['baseUrlWithAuthPath']}${string}/`;

const DEFAULT_HEADERS = {
	'content-type': 'application/json',
} as const satisfies NonNullable<HeadersOptions>;

type HttpMethod = 'get' | 'post' | 'put' | 'delete' | 'patch';

type RequestBodyOf<
	TSchema,
	TPath extends keyof TSchema,
	TMethod extends HttpMethod,
> = TMethod extends keyof TSchema[TPath]
	? TSchema[TPath][TMethod] extends {
			requestBody?: { content: { 'application/json': infer T } };
		}
		? T
		: never
	: never;

type ResponseBodyOf<
	TSchema,
	TPath extends keyof TSchema,
	TMethod extends HttpMethod,
> = TMethod extends keyof TSchema[TPath]
	? TSchema[TPath][TMethod] extends {
			responses: { 200: { content: { 'application/json': infer R } } };
		}
		? R
		: never
	: never;

type RequestPathParamsOf<
	TSchema,
	TPath extends keyof TSchema,
	TMethod extends HttpMethod,
> = TMethod extends keyof TSchema[TPath]
	? TSchema[TPath][TMethod] extends {
			parameters: { path: infer P };
		}
		? P
		: never
	: never;

type RequestQueryParamsOf<
	TSchema,
	TPath extends keyof TSchema,
	TMethod extends HttpMethod,
> = TMethod extends keyof TSchema[TPath]
	? TSchema[TPath][TMethod] extends {
			parameters: { query?: infer Q };
		}
		? Q
		: never
	: never;

export type GetRequestPathParams<TSchema, TPath extends keyof TSchema> = RequestPathParamsOf<TSchema, TPath, 'get'>;

export type GetRequestQueryParams<TSchema, TPath extends keyof TSchema> = RequestQueryParamsOf<TSchema, TPath, 'get'>;

export type GetResponseBodyJson<TSchema, TPath extends keyof TSchema> = ResponseBodyOf<TSchema, TPath, 'get'>;

export type PostRequestPathParams<TSchema, TPath extends keyof TSchema> = RequestPathParamsOf<TSchema, TPath, 'post'>;

export type PostRequestBodyJson<TSchema, TPath extends keyof TSchema> = RequestBodyOf<TSchema, TPath, 'post'>;

export type PostResponseBodyJson<TSchema, TPath extends keyof TSchema> = ResponseBodyOf<TSchema, TPath, 'post'>;

export type PutRequestPathParams<TSchema, TPath extends keyof TSchema> = RequestPathParamsOf<TSchema, TPath, 'put'>;

export type PutRequestBodyJson<TSchema, TPath extends keyof TSchema> = RequestBodyOf<TSchema, TPath, 'put'>;

export type PutResponseBodyJson<TSchema, TPath extends keyof TSchema> = ResponseBodyOf<TSchema, TPath, 'put'>;

export type DeleteRequestPathParams<TSchema, TPath extends keyof TSchema> = RequestPathParamsOf<
	TSchema,
	TPath,
	'delete'
>;

export type DeleteRequestBodyJson<TSchema, TPath extends keyof TSchema> = RequestBodyOf<TSchema, TPath, 'delete'>;

export type DeleteResponseBodyJson<TSchema, TPath extends keyof TSchema> = ResponseBodyOf<TSchema, TPath, 'delete'>;

export type PostRequestBody<TSchema, TPath extends keyof TSchema> = RequestBodyOf<TSchema, TPath, 'post'>;

export type PostResponseBody<TSchema, TPath extends keyof TSchema> = ResponseBodyOf<TSchema, TPath, 'post'>;

interface RetryableResponse {
	readonly shouldRetry: boolean;
	readonly response: Response;
}

const sessionsApi = getOrCreateApiSessionsServer();

/**
 *
 * @template TSchema - The OpenAPI schema type for the Nakama service. This should match the OpenAPI specification for the service you are connecting to.
 *
 * Usage:
 *   Instantiate with the Nakama service name, base URL, and optional additional headers.
 *   Use the client to make requests according to the provided schema.
 *
 * Constructor parameters:
 *   - nakamaServiceName: The name of the Nakama service to connect to.
 *   - baseUrl: The base URL for the Nakama service.
 *   - additionalHeaders: Optional additional headers to include in requests.
 */

export class NakamaFetchClient<TSchema> {
	private readonly nakamaServiceName: string;
	private readonly additionalHeaders: Record<string, string>;
	private readonly baseUrl: string;
	// @ts-expect-error - Legacy code, nullability not yet enforced
	private maybeClient: Client<DeepReadonly<TSchema>> | null = null;
	private isRefreshingToken = false;
	private readonly authStore = getOrCreateAuthStore();

	constructor({ nakamaServiceName, additionalHeaders = {}, baseUrl }: GetOpenapiFetchClientParams) {
		this.nakamaServiceName = nakamaServiceName;
		this.additionalHeaders = additionalHeaders;
		this.baseUrl = baseUrl;
	}

	private readonly buildBaseUrlFromConfiguration = (): NakamaBaseUrlWithAuthenticationPath => {
		const { baseUrlWithAuthPath: configBaseUrl } = getAuthenticationInfo({ baseUrl: this.baseUrl });

		return `${configBaseUrl}${this.nakamaServiceName}/`;
	};

	private readonly createAuthenticationHeaders = () => {
		const { headers: authenticationHeaders } = getAuthenticationInfo({ baseUrl: this.baseUrl });

		const defaultHeaders = {
			...DEFAULT_HEADERS,
			...this.additionalHeaders,
			...authenticationHeaders,
		};

		return defaultHeaders;
	};

	private readonly attemptTokenRefreshIfNeeded = async (): Promise<void> => {
		if (this.isRefreshingToken) {
			return;
		}

		this.isRefreshingToken = true;

		try {
			await sessionsApi.refreshSession();
		} finally {
			this.isRefreshingToken = false;
		}
	};

	private readonly executeRequestWithFreshToken = async (request: Request, body: string | null): Promise<Response> => {
		const updatedHeaders = {
			...Object.fromEntries(request.headers.entries()),
			...this.createAuthenticationHeaders(),
		};

		const retriedResponse = await fetch(request.url, {
			method: request.method,
			headers: updatedHeaders,
			body,
		});

		return retriedResponse;
	};

	private readonly shouldRetryAfterAuthRefresh = async (response: Response): Promise<RetryableResponse> => {
		switch (response.status) {
			case 401: {
				await this.attemptTokenRefreshIfNeeded();
				return {
					shouldRetry: true,
					response,
				} as const;
			}

			default:
				return {
					shouldRetry: false,
					response,
				} as const;
		}
	};

	private readonly createResponseMiddleware = (): Middleware => {
		const requestBodyMap = new WeakMap<Request, string | null>();

		return {
			onRequest: async ({ request }) => {
				const headerKey = this.authStore.getIsSAML ? 'authenticationToken' : 'token';
				request.headers.set(headerKey, sessionsApi.getToken());

				if (request.body) {
					const clonedRequest = request.clone();
					requestBodyMap.set(request, await clonedRequest.text());
				} else {
					requestBodyMap.set(request, null);
				}

				return request;
			},
			onResponse: async ({ response, request }) => {
				if (response.ok) {
					return response.clone();
				}

				const { response: retryResponse, shouldRetry } = await this.shouldRetryAfterAuthRefresh(response);

				if (!shouldRetry) {
					return retryResponse.clone();
				}

				const bodyClone = requestBodyMap.get(request) ?? null;
				const withFreshTokenResponse = await this.executeRequestWithFreshToken(request, bodyClone);

				return withFreshTokenResponse.clone();
			},
		} as const satisfies Middleware;
	};

	private readonly createClientInstance = (): void => {
		const baseUrl = this.buildBaseUrlFromConfiguration();
		const headers = this.createAuthenticationHeaders();

		// @ts-expect-error - Legacy code, nullability not yet enforced
		this.maybeClient = createClient<DeepReadonly<TSchema>>({
			baseUrl,
			headers,
		});

		this.maybeClient.use(this.createResponseMiddleware());
	};

	// @ts-expect-error - Legacy code, nullability not yet enforced
	readonly getOrCreateClient = (): Client<DeepReadonly<TSchema>> => {
		if (!this.maybeClient) {
			this.createClientInstance();
		}

		if (!this.maybeClient) {
			throw new Error('Failed to create NakamaFetchClient client instance');
		}

		return this.maybeClient;
	};
}

// @ts-expect-error - Legacy code, nullability not yet enforced
export function getNakamaFetchClient<TSchema>(props: GetOpenapiFetchClientParams): Client<DeepReadonly<TSchema>> {
	const nakamaFetcher = new NakamaFetchClient<TSchema>(props);

	return nakamaFetcher.getOrCreateClient();
}
